
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Book, BookPage } from '@/types/book';

// Type guard to check if something is a non-null object
function isObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Helper function to check if an object has pages array
function hasPages(obj: any): obj is { pages: any[] } {
  return isObject(obj) && Array.isArray(obj.pages);
}

// Helper function to extract URL from imageUrl, whether it's a string or object
function extractImageUrl(imageUrl: string | { url: string; [key: string]: any } | undefined): string | undefined {
  if (!imageUrl) return undefined;
  if (typeof imageUrl === 'string') return imageUrl;
  if ('url' in imageUrl) return imageUrl.url;
  return undefined;
}

// Function to fetch book content directly from Supabase
export const fetchBookContent = async (bookId: number): Promise<Book> => {
  console.log(`Starting fetch for book: ${bookId}`);
  
  const { data, error } = await supabase
    .from('books')
    .select(`
      id,
      title,
      author,
      description,
      short_description,
      compiled_book,
      cover_image_url,
      file_path,
      price,
      bundle_id,
      bundle_price,
      availability_type,
      is_preview,
      created_at,
      updated_at,
      image_generator,
      reference_movie,
      image_frequency,
      character_descriptions,
      soundtrack,
      studio_intro
    `)
    .eq('id', bookId)
    .single();
  
  if (error) {
    console.error('Error fetching book data:', error);
    throw error;
  }

  if (!data) {
    console.error('No data returned for book ID:', bookId);
    throw new Error(`No data found for book ID: ${bookId}`);
  }

  console.log(`Book data received for '${data.title}', checking for truncation...`);
  
  // Check if compiled_book exists and log its size
  if (data.compiled_book) {
    const compiledBookStr = JSON.stringify(data.compiled_book);
    console.log(`Compiled book data size: ${compiledBookStr.length} characters`);
    
    // Check for potential truncation
    if (isObject(data.compiled_book) && hasPages(data.compiled_book)) {
      const pagesCount = data.compiled_book.pages.length;
      console.log(`Book has ${pagesCount} pages defined`);
      
      // Check the last page to see if it looks complete
      if (pagesCount > 0) {
        const lastPage = data.compiled_book.pages[pagesCount - 1];
        if (typeof lastPage === 'string' && lastPage.length < 10) {
          console.warn(`Last page appears truncated: "${lastPage}"`);
        }
      }
      
      // Count empty pages
      let emptyPageCount = 0;
      data.compiled_book.pages.forEach((page: any, index: number) => {
        if (!page || (typeof page === 'string' && page.trim() === '') || 
            (typeof page === 'object' && (!page.content || page.content.trim() === ''))) {
          emptyPageCount++;
          console.warn(`Empty page detected at index ${index}`);
        }
      });
      
      if (emptyPageCount > 0) {
        console.warn(`Book contains ${emptyPageCount} empty pages out of ${pagesCount} total pages`);
      }

      // Log paragraph formatting information if available
      data.compiled_book.pages.forEach((page: any, index: number) => {
        if (page && page.formatting && page.formatting.paragraphBreaks) {
          console.log(`Page ${index + 1} has paragraph formatting: ${page.formatting.paragraphBreaks.length} breaks, alignment: ${page.formatting.alignment || 'default'}`);
        }
      });
    } else {
      console.warn('Book has no pages array or it is empty');
    }
  } else {
    console.warn('No compiled_book data found for this book');
  }

  // Process and standardize the compiled_book structure
  if (data.compiled_book) {
    try {
      // Make sure compiled_book is an object
      let compiledBook: any = data.compiled_book;
      
      // Parse if it's a string
      if (typeof compiledBook === 'string') {
        compiledBook = JSON.parse(compiledBook);
        console.log('Parsed compiled_book from string format');
      }
      
      // Handle both object and array formats
      if (compiledBook) {
        // Initialize imagesByPage if it doesn't exist
        if (!compiledBook.imagesByPage) {
          compiledBook.imagesByPage = {};
        }
        
        // MODIFIED: Skip info pages - always set specialPageCount to 1 (just cover)
        const specialPageCount = 1; // Only count the cover page
        console.log(`Setting specialPageCount to ${specialPageCount} (cover only, ignoring info pages)`);
        
        // Initialize or update metadata
        if (!compiledBook.metadata) {
          compiledBook.metadata = {
            totalPages: Array.isArray(compiledBook.pages) ? compiledBook.pages.length : 0,
            hasImages: false,
            template: compiledBook.template || 'default',
            chapterPages: compiledBook.chapterPages || [],
            paragraphsPerPage: compiledBook.paragraphsPerPage || 4,
            specialPageCount: specialPageCount
          };
        } else {
          // Update existing metadata
          compiledBook.metadata.specialPageCount = specialPageCount;
        }
        
        // Create imagesByPage lookup if we have images
        // New format with images.pages
        if (isObject(compiledBook) && 
            isObject(compiledBook.images) && 
            Array.isArray(compiledBook.images.pages)) {
          
          console.log(`Processing ${compiledBook.images.pages.length} images in new format`);
          console.log(`Special page count: ${specialPageCount}`);
          
          // Process images.pages array (preferred format)
          compiledBook.images.pages.forEach((img: any) => {
            if (img && img.pageNumber !== undefined) {
              // Store by both number and string key to ensure we can access it either way
              compiledBook.imagesByPage[img.pageNumber] = img;
              compiledBook.imagesByPage[String(img.pageNumber)] = img;
              
              // For legacy support, also add page_X format
              compiledBook.imagesByPage[`page_${img.pageNumber}`] = img;
              compiledBook.imagesByPage[`page${img.pageNumber}`] = img;
              
              console.log(`Mapped image for page ${img.pageNumber}: ${extractImageUrl(img)}`);
              
              // If this is the first story page image (after special pages)
              if (img.pageNumber === specialPageCount + 1) {
                // Also map it to story page 1
                compiledBook.imagesByPage["story_1"] = img;
                console.log(`Mapped first story image to key "story_1"`);
              }
            }
          });
          
          // Ensure images has imagesByPage
          if (compiledBook.images) {
            compiledBook.images.imagesByPage = { ...compiledBook.imagesByPage };
          }
        }
        
        // Legacy format with direct images array
        else if (isObject(compiledBook) && 
                Array.isArray(compiledBook.images)) {
          console.log(`Processing ${compiledBook.images.length} images in legacy format`);
          
          compiledBook.images.forEach((img: any, index: number) => {
            if (img) {
              // Extract page number from image data
              const pageNumber = img.pageNumber || 
                                (img.pageName ? parseInt(img.pageName.replace(/\D/g, '')) || index + 1 : index + 1);
              
              // Store with multiple key formats for maximum compatibility
              compiledBook.imagesByPage[pageNumber] = img;
              compiledBook.imagesByPage[String(pageNumber)] = img;
              compiledBook.imagesByPage[`page_${pageNumber}`] = img;
              compiledBook.imagesByPage[`page${pageNumber}`] = img;
              
              // Also store by index for direct access
              compiledBook.imagesByPage[index] = img;
              compiledBook.imagesByPage[String(index)] = img;
              
              // If this is the first story page image (after special pages)
              if (pageNumber === specialPageCount + 1) {
                // Also map it to story page 1
                compiledBook.imagesByPage["story_1"] = img;
                console.log(`Mapped first story image to key "story_1"`);
              }
            }
          });
        }
        
        // Log image mapping for debugging
        console.log('Created imagesByPage mapping with keys:', Object.keys(compiledBook.imagesByPage));
        
        // Verify first story image is mapped correctly
        const firstStoryImageKey = specialPageCount + 1;
        const firstStoryImage = compiledBook.imagesByPage[firstStoryImageKey];
        if (firstStoryImage) {
          console.log(`First story image (page ${firstStoryImageKey}) found: ${extractImageUrl(firstStoryImage)}`);
        } else {
          console.warn(`No image found for first story page (${firstStoryImageKey})`);
        }
        
        // Update with processed compiled_book
        data.compiled_book = compiledBook;
      }
    } catch (err) {
      console.error('Error processing compiled_book:', err);
      // Don't throw here, just log error and continue with original data
    }
  }
  
  // Return the data with proper type casting
  return data as unknown as Book;
};

// Hook to use in components
export function useBookData(bookId: number) {
  return useQuery({
    queryKey: ['book-content', bookId],
    queryFn: () => fetchBookContent(bookId),
    enabled: !!bookId,
    staleTime: 60000, // Cache for 1 minute
    retry: 1, // Only retry once to avoid repeated attempts on truncated data
  });
}
